import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useReducer, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import styled, { css } from 'styled-components';
import { card, Flex, FlexCenter, FlexColumn, flexMiddle, Loader } from '@ubeya/shared-web/components';
import { maxMedia } from '@ubeya/shared-web/style';
import { selectedBranch } from '@ubeya/shared/atoms/shared';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import privileges from '@ubeya/shared-web/privileges';
import PrivilegesContext from '@ubeya/shared-web/contexts/PrivilegesContext';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { schedulingGroupBy, schedulingHourlyDate, timeAvailabilityByEmployeeTimeRange } from 'shared/atoms/scheduling';
import DefaultHeaderRenderer from './HeaderRenderer';
import { EditSpecificBookingModal } from 'scheduling/components/EditSpecificBookingModal';
import useTimeAvailabilityBookingOptions from 'scheduling/hooks/useTimeAvailabilityBookingOptions';
import { GROUP_BY_EMPLOYEE_VIEW, GROUP_BY_OPTIONS } from 'scheduling/constants';

const TIME_PICKER_INTERVAL = 15;

const Wrapper = styled(FlexColumn)`
  ${card};
  height: 100%;
  flex: 1;
  overflow: hidden;

  opacity: ${({ isLoading }) => (isLoading ? 0.3 : 1)};
  margin-bottom: 20px;
`;

const ChilrenWrapper = styled.div``;
const ExtraRowWrapper = styled.div``;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: hidden;
`;

const Content = styled(FlexColumn)`
  flex: 1;
  overflow: auto;

  height: 100%;
  ${maxMedia.mobile`min-width:1200px;`};
`;

const LoaderWrapper = styled(FlexCenter)`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 60px;
  right: 0;
  z-index: 2;
`;

const Row = styled(Flex)`
  position: relative;
  min-height: 60px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};

  ${({ isFull }) => isFull && 'flex: 1;'};
  ${({ isHeader, showStats }) =>
    isHeader &&
    `position: sticky; top: ${showStats ? 101 : 0}px; z-index:2; background: ${({ theme }) => theme.colors.white};`};
`;

const GroupRow = styled(Row)`
  ${flexMiddle};
  min-height: ${({ isHeader }) => (isHeader ? 60 : 40)}px;
  justify-content: center;
  background: ${({ color = '#fff' }) => color};
  color: ${({ theme, placeHolder }) => (placeHolder ? theme.colors.primary : theme.colors.black)};
  cursor: ${({ placeHolder }) => (placeHolder ? 'pointer' : 'default')};
  position: sticky;
  top: 0;
  z-index: 2;
`;

const Cell = styled.div.attrs(() => ({ className: 'cell' }))`
  display: block;
  flex: 1 0 0%;
  min-width: 0px;
  &:not(:first-child) {
    border-left: 1px solid ${({ theme }) => theme.colors.grey2};
  }

  ${({ isToday }) =>
    isToday &&
    css`
      background-color: ${({ theme }) => theme.colors.grey1};
    `}
`;

const HeaderCell = styled(Cell)`
  background-color: ${({ theme }) => theme.colors.grey1};
`;

const RowName = styled(Cell)`
  text-align: left;
  position: relative;
  background: ${({ color, theme }) => color || theme.colors.white};
`;

const isOpen = (map, id) => map[id] === undefined || map[id] === true;

const WeekView = ({
  isLoading,
  startDate,
  endDate,
  refetch,
  data = [],
  projects,
  StatsRenderer,
  HeaderRenderer = DefaultHeaderRenderer,
  GroupRenderer,
  hasTimeAvailability,
  RowRenderer,
  DayRenderer,
  projectsByDate,
  mappedLabels,
  labels,
  extraRow,
  extraColumns = [],
  scrollRef,
  firstColumnHeaderRenderer,
  addExtraRow = true,
  showLeftRow = true
}) => {
  const [isEditBookingOpen, setIsEditBookingOpen] = useState(false);
  const myPrivileges = useContext(PrivilegesContext);
  const { theme } = useContext(ConfigContext);

  const { mappedBranches } = useBranches();
  const selectedBranchId = useRecoilValue(selectedBranch);

  const { workDays: selectedWorkDays } = mappedBranches[selectedBranchId]?.timesheetConfig || [];
  const { bookingActionsFactory } = useTimeAvailabilityBookingOptions({ refetch });

  const setHourlyViewDate = useSetRecoilState(schedulingHourlyDate);
  const setTimeAvailabilityByEmployee = useSetRecoilState(timeAvailabilityByEmployeeTimeRange);
  const [groupBy, setGroupBy] = useRecoilState(schedulingGroupBy);

  const allDaysInWeek = useMemo(() => [...Array(7)].map((_, index) => index + 1), []);
  const workDays = useMemo(
    () => (selectedWorkDays && selectedWorkDays?.length > 0 ? selectedWorkDays : allDaysInWeek),
    [allDaysInWeek, selectedWorkDays]
  );
  const getEmployeesShifts = useCallback(
    (employeeId, date) =>
      data?.find(({ groupId }) => groupId === employeeId)?.shifts[moment(date).format('YYYY-MM-DD')],
    [data]
  );

  const isBiweekly = moment(endDate).diff(moment(startDate), 'days') + 1 > 7;

  const onDayClick = useCallback(
    (date) => {
      setHourlyViewDate(date);
      setGroupBy(GROUP_BY_OPTIONS.EMPLOYEE);
      setTimeAvailabilityByEmployee(GROUP_BY_EMPLOYEE_VIEW.DAILY);
    },
    [setHourlyViewDate, setGroupBy, setTimeAvailabilityByEmployee]
  );

  const columns = useMemo(() => {
    const days = moment(endDate).diff(moment(startDate), 'days') + 1;
    const start = moment(startDate).add(-1, 'day');

    return (
      [...Array(days)]
        .map((_, index) => {
          const date = moment(start.add(1, 'day'));
          const formattedDate = date.format(API_DATE_FORMAT);

          return {
            date,
            formattedDate,
            onDayClick,
            projects: projectsByDate?.[formattedDate] || [],
            index
          };
        })
        // disaply in UI only the relevent working days
        // in workDay array: Sunday is 1, in moment.day() Sunday is 0
        .filter(({ date }) => workDays.includes(date.day() + 1))
    );
  }, [endDate, onDayClick, projectsByDate, startDate, workDays]);

  const [expandGroup, setExpandGroup] = useReducer(
    (state, payload) => ({ ...state, [payload]: !isOpen(state, payload) }),
    {}
  );

  const [expandRow, setExpandRow] = useReducer(
    (state, payload) => ({ ...state, [payload]: !isOpen(state, payload) }),
    {}
  );

  const children = useMemo(
    () =>
      data.map(({ groupId, color, rows, ...rest }, index) => (
        <React.Fragment key={groupId}>
          {GroupRenderer && (
            <GroupRow color={color} index={index} showStats={!!StatsRenderer}>
              <GroupRenderer
                value={groupId}
                isExpended={isOpen(expandGroup, groupId)}
                toggleRow={() => setExpandGroup(groupId)}
              />
            </GroupRow>
          )}

          {isOpen(expandGroup, groupId) &&
            (rows.length > 0 ? rows : [{}]).map(({ rowId, color: rowColor }) => (
              <Row key={rowId || ''}>
                {showLeftRow && (
                  <RowName color={rowColor}>
                    {rowId !== undefined && RowRenderer && (
                      <RowRenderer
                        {...rest}
                        value={rowId}
                        isExpended={isOpen(expandRow, `${groupId}-${rowId}`)}
                        toggleRow={() => setExpandRow(`${groupId}-${rowId}`)}
                      />
                    )}
                  </RowName>
                )}
                {extraColumns.map(({ CellRenderer }, extraColumnIndex) => (
                  <Cell key={`${groupId}-${rowId}-${extraColumnIndex}`} style={{ maxWidth: 100 }}>
                    <CellRenderer group={groupId} row={rowId} {...rest} />
                  </Cell>
                ))}
                {columns.map((column, columnIndex) => {
                  const isToday = column?.date && moment().isSame(column.date, 'date');

                  return (
                    <Cell key={`${groupId}-${rowId}-${columnIndex}`} isToday={isToday}>
                      {DayRenderer && (
                        <DayRenderer
                          {...column}
                          group={groupId}
                          row={rowId}
                          expandRow={isOpen(expandRow, `${groupId}-${rowId}`)}
                          onEditSlot={setIsEditBookingOpen}
                          hasTimeAvailability={hasTimeAvailability}
                        />
                      )}
                    </Cell>
                  );
                })}
              </Row>
            ))}
        </React.Fragment>
      )),
    [
      data,
      GroupRenderer,
      StatsRenderer,
      expandGroup,
      showLeftRow,
      RowRenderer,
      expandRow,
      extraColumns,
      columns,
      DayRenderer,
      hasTimeAvailability
    ]
  );

  const showClientPayrollStats = useMemo(() => myPrivileges[privileges.CRM], [myPrivileges]);

  return (
    <Wrapper isLoading={isLoading}>
      {isLoading && (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      )}

      <Container>
        {StatsRenderer && (
          <Row isHeader>
            <Cell>
              <StatsRenderer
                showClientPayrollStats={showClientPayrollStats}
                isHeader
                dates={columns.map(({ formattedDate }) => formattedDate)}
                showAdditionalExpenses={groupBy === GROUP_BY_OPTIONS.SHIFT}
              />
            </Cell>

            {extraColumns.map((column, index) => (
              <Cell key={index} style={{ maxWidth: 100 }}>
                <StatsRenderer
                  showClientPayrollStats={showClientPayrollStats}
                  isExtraColumn
                  showAdditionalExpenses={groupBy === GROUP_BY_OPTIONS.SHIFT}
                />
              </Cell>
            ))}

            {columns.map((column, index) => (
              <Cell key={index}>
                <StatsRenderer
                  {...column}
                  showClientPayrollStats={showClientPayrollStats}
                  showAdditionalExpenses={groupBy === GROUP_BY_OPTIONS.SHIFT}
                />
              </Cell>
            ))}
          </Row>
        )}

        <Row isHeader showStats={!!StatsRenderer}>
          {showLeftRow && <HeaderCell>{firstColumnHeaderRenderer?.()}</HeaderCell>}

          {extraColumns.map(({ HeaderRenderer: HeaderRendererComponent }, index) => (
            <HeaderCell key={index} style={{ backgroundColor: theme.colors.white, maxWidth: 100 }}>
              <HeaderRendererComponent />
            </HeaderCell>
          ))}

          {columns.map((column, index) => (
            <HeaderCell key={index} style={{ backgroundColor: theme.colors.white }}>
              <HeaderRenderer {...column} isBiweekly={isBiweekly} />
            </HeaderCell>
          ))}
        </Row>

        {extraRow && (
          <Row isHeader style={{ top: extraRow?.top }}>
            <Cell>{extraRow?.extraRowFirstCell?.()}</Cell>

            {extraColumns.map((column, index) => (
              <Cell key={index} style={{ maxWidth: 100 }} />
            ))}

            {columns.map((column, index) => (
              <Cell key={index}>{extraRow?.render?.(column)}</Cell>
            ))}
          </Row>
        )}

        <Content ref={scrollRef}>
          <ChilrenWrapper>{children}</ChilrenWrapper>

          <ExtraRowWrapper>
            {addExtraRow && (
              <Row isFull>
                {showLeftRow && <Cell />}

                {extraColumns.map((column, index) => (
                  <Cell key={index} style={{ maxWidth: 100 }} />
                ))}

                {columns.map((column, index) => {
                  const isToday = column?.date && moment().isSame(column.date, 'date');
                  return (
                    <Cell key={index} isToday={isToday}>
                      {DayRenderer && <DayRenderer {...column} />}
                    </Cell>
                  );
                })}
              </Row>
            )}
          </ExtraRowWrapper>
        </Content>
      </Container>

      {isEditBookingOpen && (
        <EditSpecificBookingModal
          //   shift={{ ...isEditBookingOpen, ...isEditBookingOpen?.shift, id: isEditBookingOpen?.slotId } || {}}
          slot={isEditBookingOpen}
          onClose={() => setIsEditBookingOpen(false)}
          refetch={refetch}
          labels={labels}
          selectedBranchId={selectedBranchId}
          projects={projects}
          mappedLabels={mappedLabels}
          bookingActionsFactory={bookingActionsFactory}
          getEmployeesShifts={getEmployeesShifts}
          interval={TIME_PICKER_INTERVAL}
        />
      )}
    </Wrapper>
  );
};

WeekView.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      groupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      rows: PropTypes.arrayOf(PropTypes.shape({ rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) }))
    })
  ),
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired
};

export default React.memo(WeekView);
